import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import {
  activeDict,
  i18nBootstrapScript,
  serverSlicedPayload,
  whenDictReady,
} from "@/lib/i18n-dict";
import { getCurrentLocale, ALTERNATE_LANGS } from "@/lib/utils/locale";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { StoreCartProvider } from "@/context/StoreCartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { DirectionProvider } from "@radix-ui/react-direction";
import { I18nProvider, LANGUAGES, useI18n } from "@/context/I18nContext";
import { ConsentBanner } from "@/components/layout/ConsentBanner";
import { NotFoundView } from "@/components/layout/NotFoundView";
import { trackPageView, initTrackingConsentListener } from "@/lib/tracking";
import { initContactLinkTracking } from "@/lib/analytics-events";
import { captureCampaignParams } from "@/lib/submission-context";
import { loadGtm, pushPageView, initGtmConsentListener } from "@/lib/gtm";
import { loadUmami, trackUmamiPageView } from "@/lib/umami";
import { SiteShell } from "@/components/layout/SiteShell";
import { getLocalizedPageMeta, localizedCompanyName } from "@/lib/utils/seo";
import type { SeoPage } from "@/lib/utils/seo";
import { COMPANY } from "@/lib/company";
import { SITE_URL } from "@/lib/utils/site-url";
import { readCanonicalOverride, readNotFound } from "@/lib/utils/canonical-override";

function NotFoundComponent() {
  // The failed path must come from the server: NotFoundView reads
  // window.location during SSR otherwise, which is undefined there, so every
  // 404 rendered the generic featured list instead of guessing from the URL —
  // the whole point of the page. serverStorage already carries the path for
  // i18n; reuse it rather than adding a second channel.
  return <NotFoundView pathname={getServerPath()} />;
}

function getServerPath(): string | undefined {
  if (typeof window !== "undefined") return window.location.pathname;
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => { path?: string } | undefined } | undefined;
  return storage?.getStore()?.path;
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const lang = getCurrentLocale();
  const dict = activeDict();
  const homeHref = lang === "en" ? "/" : `/${lang}`;

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {dict["error.title"] ?? "This page couldn't load"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {dict["error.description"] ??
            "Something went wrong. You can try again or return to the home page."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {dict["error.tryAgain"] ?? "Try again"}
          </button>
          <a
            href={homeHref}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {dict["error.home"] ?? "Home"}
          </a>
        </div>
      </div>
    </div>
  );
}

/** Stable identity for the company node, so every schema block that mentions
 *  VARS points at one entity rather than minting a parallel one. */
export const ORGANIZATION_ID = "https://varsco.com/#organization";

/**
 * The company entity. Every other schema block on the site should reference
 * this node by @id rather than restating the organization, so Google resolves
 * one entity instead of several similar ones.
 */
function organizationSchema(lang: ReturnType<typeof getCurrentLocale>) {
  const companyName = localizedCompanyName(lang);
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    // `name` is the trade name the site brands itself with in every language.
    // `legalName` is the registered company — different string, same entity —
    // and it is the property that exists precisely so the two do not have to be
    // reconciled in visible copy. Both were already public: the legal name and
    // tax ID sit in SiteFooter, and the tax office and MERSİS numbers are on the
    // KVKK and distance-sales pages, where Turkish law requires them.
    name: companyName,
    legalName: COMPANY.legalName,
    alternateName:
      lang === "tr"
        ? [COMPANY.compactName, "VARS Co."]
        : [COMPANY.shortName, COMPANY.compactName, "VARS Co."],
    taxID: COMPANY.taxId,
    identifier: {
      "@type": "PropertyValue",
      name: "MERSİS",
      value: COMPANY.mersis,
    },
    url: "https://varsco.com",
    // favicon.ico is not a usable logo: Google wants a real raster image it can
    // show, and an .ico is neither the right format nor the right dimensions.
    logo: "https://varsco.com/vars-logo.png",
    description:
      lang === "tr"
        ? "Canlı yem, kuluçkahane girdileri, somon yumurtası ve Akdeniz su ürünleri için Türkiye merkezli B2B tedarikçi."
        : "B2B supplier of bio-enhanced live feed, hatchery inputs, salmon eggs, and Mediterranean seafood shipped worldwide from Türkiye.",
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.address.street,
      addressLocality: COMPANY.address.locality,
      postalCode: COMPANY.address.postalCode,
      addressCountry: COMPANY.address.country,
    },
    contactPoint: {
      "@type": "ContactPoint",
      // Was +90-232-000-0000, a placeholder. Structured data is an entity-trust
      // signal, and a number that contradicts the one in the header, the footer
      // and the contact page is worse than no contactPoint at all. This is the
      // office line those three already publish.
      telephone: "+90 232 290 57 56",
      contactType: "sales",
      email: "info@varsco.com",
      availableLanguage: ["English", "Turkish", "Arabic", "Russian"],
    },
  };
}

// Imported, not redeclared. This was the last hardcoded copy of four, and the
// only one that ignored VITE_SITE_URL entirely — so a staging build emitted
// production canonicals, hreflang and Organization @ids while the sitemap on
// the same deployment emitted staging ones.
// 1200x630, the ratio Open Graph and Twitter cards actually crop to. This
// first pointed at vars-logo-header.png, which has never existed in public/,
// so every preview 404'd; the stopgap after that was vars-logo.png, a 600x600
// square logo that previewed as a small crest with letterboxing on both sides.
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/** Open Graph locale identifiers. og: wants language_TERRITORY, not the bare
 *  language code the URL prefix uses. */
const OG_LOCALES: Record<string, string> = {
  en: "en_US",
  tr: "tr_TR",
  ar: "ar_SA",
  de: "de_DE",
  ru: "ru_RU",
  ja: "ja_JP",
  ko: "ko_KR",
  zh: "zh_CN",
  es: "es_ES",
  fa: "fa_IR",
};

/**
 * og:locale:alternate, one tag per translation.
 *
 * These cannot go in head()'s `meta` array. TanStack keys that array by
 * `name ?? property` and keeps a single tag per key (`metaByAttribute` in
 * headContentUtils), so nine alternates collapsed into whichever the
 * reverse-order scan reached first — `fa_IR`, for no reason but its position
 * in ALTERNATE_LANGS. Open Graph specifies this property as repeatable, so
 * there is no key to make unique; the only way out of the dedupe is to render
 * the tags ourselves, which the shell can do because it owns <head> directly.
 *
 * getCurrentLocale() rather than the shell's getServerLang(): the shell also
 * renders during hydration, where serverStorage is undefined and every locale
 * would report English — the same defect VARS-009 fixed for og:locale itself.
 */
function OgLocaleAlternates() {
  const activeLang = getCurrentLocale();
  return (
    <>
      {ALTERNATE_LANGS.filter((l) => l !== activeLang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALES[l]} />
      ))}
    </>
  );
}

function getRootPageSeo(path: string) {
  const page = path.replace(/^\//, "").split("/")[0];
  const pageMap: Record<string, SeoPage> = {
    "about-us": "about",
    "services-solutions": "services",
    projects: "projects",
    "seafood-export": "seafood",
    "seafood-export-from-turkey-to-europe": "seafoodEurope",
    contactus: "contact",
    "request-quote": "quote",
    faqs: "faq",
    "salmonid-ova-solutions": "salmonOva",
    "artemia-cysts-incubation-guide": "artemiaGuide",
    "decapsulated-artemia-guide": "decapGuide",
    "horeca-seafood-middle-east": "horeca",
    "regional-trade-middle-east-europe": "regionalTrade",
    "aquariums-and-hobbyists": "aquariums",
    privacy: "privacy",
    terms: "terms",
    login: "login",
    register: "register",
    account: "account",
    cart: "cart",
    shop: "shop",
    blog: "blog",
    products: "products",
    glossary: "glossary",
    events: "expoFutureFish",
    "olive-flounder-export": "oliveFlounderExport",
  };
  return pageMap[page] ? getLocalizedPageMeta(pageMap[page]) : getLocalizedPageMeta("home");
}

/**
 * The locales a fixed-path page actually has a body in.
 *
 * Declaring a one-language document in ten locales creates ten URLs with
 * identical content whose hreflang asserts the duplication is deliberate —
 * sitemap[.]xml.ts has said so in someLocales() since VARS-009, and the head
 * kept contradicting it. VARS-062 fixed privacy and terms; KVKK and the
 * distance-sales agreement are Turkish legal instruments and were still
 * shipping ten alternates, so /ja/kvkk-disclosure-text served Turkish text
 * under lang="ja" while declaring itself one of ten translations.
 *
 * Routes whose path varies by locale do not belong here — they publish their
 * own map through canonical-override instead.
 */
function localesServedBy(contentPath: string): readonly (typeof ALTERNATE_LANGS)[number][] {
  if (/^\/(kvkk-disclosure-text|distance-sales-agreement)$/.test(contentPath)) {
    return ["tr"] as const satisfies readonly (typeof ALTERNATE_LANGS)[number][];
  }
  if (/^\/(privacy|terms)$/.test(contentPath)) {
    return ["en", "tr"] as const satisfies readonly (typeof ALTERNATE_LANGS)[number][];
  }
  return ALTERNATE_LANGS;
}

// Content path is the URL with any /<lang> prefix stripped — used to build
// self-referencing canonical + hreflang links that match how server.ts
// rewrites localized requests (see deLocalizeRequest).
function getContentPath(): string {
  if (typeof window !== "undefined") {
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (
      parts.length > 0 &&
      (ALTERNATE_LANGS as readonly string[]).includes(parts[0].toLowerCase())
    ) {
      return "/" + parts.slice(1).join("/");
    }
    return window.location.pathname;
  }
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => { path?: string } | undefined } | undefined;
  return storage?.getStore()?.path ?? "/";
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Wait for the full dictionary before a client-side navigation renders, so a
  // route whose namespaces were not inlined cannot show raw keys. Skips the
  // initial load, which needs no wait and must not be delayed — see
  // whenDictReady. Returns void because beforeLoad's return value becomes route
  // context and must be serializable.
  beforeLoad: async () => {
    await whenDictReady();
  },
  head: ({ matches }) => {
    const contentPath = getContentPath();
    // A route that knows its own URLs (the shop, whose slug is not
    // locale-invariant) publishes them from its loader; everything else
    // publishes nothing and keeps the prefix-substitution default below.
    const override = readCanonicalOverride(contentPath);
    const pathSuffix = contentPath === "/" ? "" : contentPath;
    // getContentPath() strips the locale prefix, so pathSuffix alone points at
    // the ENGLISH URL. Canonical and og:url must be self-referencing: pointing
    // every localized page at the English one tells Google the translations
    // are duplicates, which makes it ignore the hreflang set on this same page
    // and drop all eight non-English locales from the index.
    const activeLang = getCurrentLocale();
    const localePrefix = activeLang === "en" ? "" : `/${activeLang}`;
    const selfUrl = `${SITE_URL}${localePrefix}${override?.selfPath ?? pathSuffix}`;
    // `matches` is snapshotted before the loaders run, so it only catches
    // globalNotFound (set at match-creation time). readNotFound() covers the
    // seven routes that decide they are a 404 inside their loader — see
    // publishNotFound().
    const isNotFound =
      matches.some((routeMatch) => routeMatch.status === "notFound" || routeMatch.globalNotFound) ||
      readNotFound(contentPath);
    const dict = activeDict();
    const pageMeta = isNotFound
      ? {
          title:
            activeLang === "tr"
              ? `Sayfa Bulunamadı | ${COMPANY.compactName}`
              : `Page Not Found | ${COMPANY.tradeName}`,
          description:
            dict["notFound.description"] ??
            "The requested page could not be found. Browse the product catalogue or request a quote.",
        }
      : getRootPageSeo(contentPath);
    // Keep this in step with every route that sets robots noindex for itself.
    // /shop/order-confirmed did so (shop.order-confirmed.tsx) but was missing
    // here, so it shipped noindex *and* a canonical, ten hreflang links and an
    // x-default — the exact conflicting-annotation case the comment further
    // down says this guard exists to prevent.
    // `/stand` is in here for a different reason than the rest: it is not
    // private, it is signage. A screen bolted to a trade stand is not a page
    // anyone should reach from a search result, and it carries no content of
    // its own — every product on it has a real page that should rank instead.
    // Landing in this set also suppresses its canonical and hreflang, which is
    // what we want: there is nothing for it to be the canonical of.
    const isPrivate =
      isNotFound ||
      /^(\/account(?:\/|$)|\/panel(?:\/|$)|\/cart$|\/login$|\/register$|\/shop\/checkout$|\/shop\/order-confirmed$|\/stand(?:\/|$))/.test(
        contentPath,
      );
    // Restricted to the languages whose BODY exists — see isBilingualLegalPage.
    const baseHreflangLangs = localesServedBy(contentPath);
    // A published locale map is authoritative: a locale it does not list gets
    // NO alternate. Emitting a guessed URL for a locale we cannot name is the
    // defect this exists to remove — and on /shop those guesses resolve 200.
    const hreflangLangs = override?.perLocale
      ? baseHreflangLangs.filter((l) => override.perLocale![l])
      : baseHreflangLangs;

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: pageMeta.title },
        {
          name: "description",
          content: pageMeta.description,
        },
        { name: "author", content: localizedCompanyName(activeLang) },
        {
          name: "keywords",
          content:
            "aquaculture, live feed, Artemia, Chlorella, salmon eggs, seafood export, B2B, Türkiye",
        },
        { property: "og:site_name", content: localizedCompanyName(activeLang) },
        {
          property: "og:title",
          content: pageMeta.title,
        },
        {
          property: "og:description",
          content: pageMeta.description,
        },
        { property: "og:type", content: "website" },
        // activeLang, not the server-only serverStorage value: this head runs on
        // client-side navigation too, where serverStorage is undefined and every
        // locale silently reported og:locale=en_US. getCurrentLocale() is
        // isomorphic and already drives canonical and hreflang three lines up,
        // so using it here keeps all three agreeing on one answer.
        { property: "og:locale", content: OG_LOCALES[activeLang] ?? "en_US" },
        // og:locale:alternate is emitted by <OgLocaleAlternates /> in the
        // shell, not here — see the comment on that component.
        { property: "og:url", content: selfUrl },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:alt", content: localizedCompanyName(activeLang) },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:title",
          content: pageMeta.title,
        },
        {
          name: "twitter:description",
          content: pageMeta.description,
        },
        { name: "twitter:image", content: OG_IMAGE },
        ...(isPrivate ? [{ name: "robots", content: "noindex, nofollow" }] : []),
      ],
      links: [
        // Assets stay in their own block, deliberately outside the conditional
        // below. If a future edit slid one of these inside it, private pages
        // would ship unstyled.
        { rel: "stylesheet", href: appCss },
        { rel: "icon", href: "/favicon.png", type: "image/png" },
        { rel: "alternate icon", href: "/favicon.ico", type: "image/x-icon" },
        // No preload for the dictionary remainder, deliberately.
        //
        // A `<link rel="preload" as="fetch">` looked like a free head start:
        // begin the download at parse time rather than waiting for hydration
        // to ask. In practice it produced two requests per page load rather
        // than one — the preload plus the real fetch — and the e2e suite went
        // from 5.0 to 7.3 minutes against the same build. Adding `crossorigin`
        // to make the preload match the CORS-mode fetch did not collapse them.
        //
        // The prize was a few hundred milliseconds; the cost was a second
        // ~400 KB download on every cold page view. `hydrateFullDict()` runs
        // from an effect straight after hydration, which is early enough.
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        {
          rel: "preload",
          as: "style",
          href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap",
        },
        // Indexing signals, emitted only for pages that are actually
        // indexable. The same head() marks account, panel, cart, login,
        // register and checkout noindex — and then used to hand Google a
        // canonical, nine hreflang links and an x-default for them anyway.
        // hreflang on a noindex page is a conflicting annotation and gets the
        // group dropped; a canonical on one asks Google to consolidate signals
        // onto a URL it has been told to exclude. Emitting neither is the only
        // unambiguous option.
        ...(isPrivate
          ? []
          : [
              { rel: "canonical", href: selfUrl },
              // Bilingual legal pages declare the two languages they actually
              // serve. Emitting all ten here said the English document was a
              // German, Persian and Korean translation of itself — a
              // duplicate-content set with the hreflang asserting the
              // duplication was deliberate. sitemap[.]xml.ts has restricted
              // these to ["tr", "en"] since VARS-009; the in-page head kept
              // contradicting it (VARS-062).
              ...hreflangLangs.map((l) => ({
                rel: "alternate",
                // The URL prefix stays "zh", but hreflang needs a script
                // subtag: bare "zh" does not tell Google whether the page is
                // Simplified or Traditional. This content is Simplified.
                // sitemap[.]xml.ts must keep saying zh-Hans too.
                hrefLang: l === "zh" ? "zh-Hans" : l,
                href: `${SITE_URL}${l === "en" ? "" : "/" + l}${override?.perLocale?.[l] ?? pathSuffix}`,
              })),
              ...(hreflangLangs.includes("en" as (typeof ALTERNATE_LANGS)[number])
                ? [
                    {
                      rel: "alternate",
                      hrefLang: "x-default",
                      href: `${SITE_URL}${override?.perLocale?.en ?? pathSuffix}`,
                    },
                  ]
                : []),
            ]),
      ],
      scripts: [
        // The i18n bootstrap used to live here. It now renders at the end of
        // <body> instead — see <I18nBootstrap /> in RootShell. It was 332 KB
        // of the 528 KB document and it sat at byte 6,880, so </head> did not
        // close until byte 343,170: the browser could not begin parsing body
        // markup until two thirds of the response had arrived, which defeated
        // the streaming SSR this app otherwise has switched on.
        {
          type: "application/ld+json",
          children: JSON.stringify(organizationSchema(activeLang)),
        },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const lang = getServerLang();
  return (
    <html lang={lang} dir={getServerDir(lang)}>
      <head>
        <HeadContent />
        <OgLocaleAlternates />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap"
          media="print"
          // @ts-expect-error -- inline onload attribute swaps media to all without blocking first paint
          onLoad="this.media='all'"
        />
        <noscript>
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap"
          />
        </noscript>
      </head>
      <body>
        {children}
        {/* Order is load-bearing, twice over.

            AFTER {children}: React renders the tree in order, so by the time
            this component renders, every server-rendered string has been
            looked up and recorded. That is what lets the payload be sliced to
            what the page actually used without a hand-maintained route map.

            BEFORE <Scripts />: that is where the hydration module tag goes,
            and module scripts are deferred, so this inline script has already
            set window.__VARS_I18N__ by the time hydration runs. */}
        <I18nBootstrap />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Hands this locale's dictionary slice to the browser.
 *
 * Emits nothing on the client: `serverSlicedPayload()` returns undefined
 * there, because the SSR-rendered script has already populated `window`.
 */
function I18nBootstrap() {
  const payload = serverSlicedPayload();
  if (!payload) return null;
  return (
    <script
      // The payload is JSON.parse'd from a JS string literal, so it cannot
      // terminate the surrounding element or inject markup. See
      // i18nBootstrapScript.
      dangerouslySetInnerHTML={{ __html: i18nBootstrapScript(payload) }}
    />
  );
}

function getServerLang() {
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => { lang?: string } | undefined } | undefined;
  return storage?.getStore()?.lang ?? "en";
}

/**
 * Text direction for the server-rendered <html>.
 *
 * I18nContext sets documentElement.dir in an effect, which only runs after
 * hydration — so Arabic pages were served as LTR and flipped once JS landed.
 * Crawlers and no-JS clients never saw RTL at all. Emitting it here means the
 * very first painted frame is already correct; the effect then agrees with it.
 *
 * Read from LANGUAGES rather than a second list, so a new RTL locale (Persian
 * is planned) is right by construction.
 */
function getServerDir(lang: string): "ltr" | "rtl" {
  return LANGUAGES.find((l) => l.code === lang)?.dir ?? "ltr";
}

/**
 * Tells Radix which way the page reads (VARS-118).
 *
 * `<html dir>` styles the page, but Radix primitives do not read it — they take
 * direction from this provider and default to `ltr` without one. That default is
 * not cosmetic: in Arabic and Persian it made the arrow keys walk the *wrong
 * way* through every Tabs list, and it is what decides which side a dropdown or
 * menu aligns to. Context passes through portals, so drawers and dialogs are
 * covered too.
 */
function RadixDirection({ children }: { children: React.ReactNode }) {
  const { dir } = useI18n();
  return <DirectionProvider dir={dir}>{children}</DirectionProvider>;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const routerState = useRouterState({ select: (s) => s.location.pathname });

  // Signage routes render bare and count for nothing.
  //
  // /stand is the portrait screen at a trade stand. It gets no header, footer,
  // WhatsApp bubble or consent banner, because none of them have a reader —
  // and no analytics, which matters more than it sounds: a screen left running
  // for three days of a fair would otherwise report itself as the site's most
  // engaged visitor and bury the real traffic it is there to generate.
  const isSignage = /^(?:\/[a-z]{2})?\/stand\/?$/.test(routerState);

  // Pageviews are recorded from here so client-side navigations count, not
  // just full document loads. Both are no-ops without consent.
  useEffect(() => {
    if (isSignage) return;
    // Before the pageview calls, and deliberately outside the consent gate:
    // this only reads the URL the visitor is already on and keeps it in the tab
    // so a form submitted three pages later still knows which ad paid for the
    // visit. Nothing is transmitted unless a form is actually submitted.
    captureCampaignParams();
    trackPageView(routerState);
    pushPageView(routerState);
    // Umami counts the same navigation. Its own auto-tracking is switched off
    // in the loader, so this is the single source of page views for it.
    trackUmamiPageView(routerState);
  }, [routerState, isSignage]);

  useEffect(() => initTrackingConsentListener(), []);
  useEffect(() => initContactLinkTracking(), []);

  // GTM is injected from here rather than head().scripts: emitting it server
  // side would contact Google for every visitor regardless of consent. Calling
  // loadGtm on mount picks up a visitor who consented on an earlier visit.
  useEffect(() => {
    if (isSignage) return;
    loadGtm();
    return initGtmConsentListener();
  }, [isSignage]);

  // Umami is self-hosted, first-party and cookieless, so it loads for every
  // visitor rather than waiting on the banner (VARS-122, lib/umami.ts). Still
  // injected client side: server-side emission would put the tag in the
  // cacheable HTML, where it would count a cache hit as a fresh view.
  // Deferred to idle time so the tracker fetch never competes with LCP assets.
  useEffect(() => {
    if (isSignage) return;
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(() => loadUmami(), { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = window.setTimeout(() => loadUmami(), 1500);
    return () => window.clearTimeout(timer);
  }, [isSignage]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <RadixDirection>
          <AuthProvider>
            <CartProvider>
              <StoreCartProvider>
                <WishlistProvider>
                  {isSignage ? (
                    <Outlet />
                  ) : (
                    <>
                      <SiteShell>
                        <Outlet />
                      </SiteShell>
                      <ConsentBanner />
                    </>
                  )}
                </WishlistProvider>
              </StoreCartProvider>
            </CartProvider>
          </AuthProvider>
        </RadixDirection>
      </I18nProvider>
    </QueryClientProvider>
  );
}
